import React, { useState } from 'react';
import { Typography, Card, Form, Input, Button, Radio, Space, Breadcrumb } from 'antd';
import { useNavigate } from 'react-router-dom';
import { InfoCircleOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { usersApi, CreateUserRequest } from '../services/users';
import { message } from '../utils/message';
import { useNavigationBlocker } from '../hooks/useNavigationBlocker';

const { Title, Text } = Typography;

interface PermissionFormData {
  adminName: string;
  id: string;
  password: string;
  email: string;
  phone: string;
  role: 'admin' | 'super_admin' | 'development_admin';
}

const AddPermission: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  const [duplicateChecked, setDuplicateChecked] = useState<{ id: boolean }>({
    id: false
  });
  const [idDuplicateStatus, setIdDuplicateStatus] = useState<'none' | 'available' | 'duplicate'>('none');
  const [passwordValue, setPasswordValue] = useState<string>('');
  const [isModified, setIsModified] = useState(false);

  // 네비게이션 가드 적용
  useNavigationBlocker(isModified, '/permission-management');

  // 폼 초기화 함수
  const resetForm = () => {
    form.resetFields();
    setDuplicateChecked({ id: false });
    setIdDuplicateStatus('none');
    setPasswordValue('');
  };

  const handleSubmit = async (values: PermissionFormData) => {
    // 중복 확인이 되지 않은 경우 체크
    if (!duplicateChecked.id) {
      message.warning('ID 중복 확인을 해주세요.');
      return;
    }

    setLoading(true);
    try {
      const createData: CreateUserRequest = {
        username: values.id,
        email: values.email,
        password: values.password,
        name: values.adminName,
        phone: values.phone,
        role: values.role,
      };

      const response = await usersApi.createUser(createData);

      if (response.success) {
        message.success('성공적으로 추가되었습니다.');
        setIsModified(false); // 네비게이션 가드 해제 (먼저 실행)
        resetForm(); // 폼 초기화
        // 상태 변경 후 다음 렌더링 사이클에서 navigate 실행
        setTimeout(() => {
          navigate('/permission-management');
        }, 0);
      } else {
        message.error(response.message || '사용자 추가에 실패했습니다.');
      }
    } catch (error: any) {
      console.error('사용자 생성 실패:', error);
      message.error(error.response?.data?.message || '사용자 추가 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // useNavigationBlocker가 팝업을 처리하므로 직접 navigate만 호출
    navigate('/permission-management');
  };

  const checkIdDuplicate = async () => {
    const id = form.getFieldValue('id');
    if (!id) {
      return;
    }

    if (id.length < 4) {
      return;
    }

    if (!/^[a-zA-Z0-9]+$/.test(id)) {
      return;
    }

    setCheckingDuplicate(true);
    try {
      const response = await usersApi.checkDuplicate('username', id);

      if (response.success) {
        if (response.data.isDuplicate) {
          setIdDuplicateStatus('duplicate');
          setDuplicateChecked(prev => ({ ...prev, id: false }));
        } else {
          setIdDuplicateStatus('available');
          setDuplicateChecked(prev => ({ ...prev, id: true }));
        }
      }
    } catch (error: any) {
      console.error('ID 중복 확인 실패:', error);
      message.error('ID 중복 확인 중 오류가 발생했습니다.');
    } finally {
      setCheckingDuplicate(false);
    }
  };

  // 입력 필드 변경 시 중복 확인 상태 초기화
  const handleFieldChange = (field: 'id') => {
    setDuplicateChecked(prev => ({
      ...prev,
      [field]: false
    }));
    setIdDuplicateStatus('none');
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* 브레드크럼 */}
      <Breadcrumb
        items={[
          {
            title: '권한 관리',
            href: '/permission-management',
          },
          {
            title: '권한 추가',
          },
        ]}
        style={{ marginBottom: '8px' }}
      />

      {/* 페이지 제목 */}
      <Title level={2} style={{ margin: '0 0 32px 0' }}>
        권한 추가
      </Title>

      {/* 폼 카드 */}
      <Card
        style={{
          background: '#1f1f1f',
          border: '1px solid #303030'
        }}
        styles={{ body: { padding: '40px' } }}
      >
        <div style={{ marginBottom: '32px' }}>
          <Title level={4} style={{ margin: '0 0 24px 0', color: '#ffffff' }}>
            기본 정보
          </Title>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onValuesChange={() => setIsModified(true)}
          requiredMark={false}
          size="large"
        >
          {/* 관리자명 */}
          <Form.Item
            label={<Text style={{ color: '#ffffff' }}>관리자명 <span style={{ color: '#ff4d4f' }}>*</span></Text>}
            name="adminName"
            rules={[
              { required: true, message: '관리자명을 입력하세요.' }
            ]}
            style={{ maxWidth: '600px' }}
          >
            <Input
              placeholder="관리자명을 입력하세요."
              style={{
                height: '48px',
                background: '#141414',
                border: '1px solid #303030',
                color: '#ffffff'
              }}
            />
          </Form.Item>

          {/* ID */}
          <Form.Item
            label={<Text style={{ color: '#ffffff' }}>ID <span style={{ color: '#ff4d4f' }}>*</span></Text>}
            name="id"
            rules={[
              { required: true, message: 'ID를 입력하세요.' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();

                  // 1순위: 영문과 숫자만 입력 가능
                  if (!/^[a-zA-Z0-9]+$/.test(value)) {
                    return Promise.reject('영문과 숫자만 입력 가능합니다.');
                  }

                  // 2순위: 최소 4자 이상
                  if (value.length < 4) {
                    return Promise.reject('최소 4자 이상이어야 합니다.');
                  }

                  return Promise.resolve();
                }
              }
            ]}
            validateStatus={idDuplicateStatus === 'duplicate' ? 'error' : undefined}
            help={
              idDuplicateStatus === 'duplicate' ? (
                <span style={{ color: '#ff4d4f', fontSize: '14px' }}>중복된 아이디가 있습니다.</span>
              ) : idDuplicateStatus === 'available' ? (
                <span style={{ color: '#1890ff', fontSize: '14px' }}>사용 가능한 아이디입니다.</span>
              ) : undefined
            }
            style={{ maxWidth: '100%' }}
          >
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <Input
                placeholder="ID를 입력하세요."
                onChange={() => handleFieldChange('id')}
                style={{
                  height: '48px',
                  maxWidth: '600px',
                  background: '#141414',
                  border: '1px solid #303030',
                  color: '#ffffff'
                }}
              />
              <Button
                type="primary"
                onClick={checkIdDuplicate}
                loading={checkingDuplicate}
                style={{
                  height: '48px',
                  minWidth: '100px',
                  background: '#1890ff',
                  border: 'none'
                }}
              >
                중복 확인
              </Button>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', height: '48px' }}>
                <InfoCircleOutlined style={{ color: '#1890ff', fontSize: '16px' }} />
                <Text style={{ color: '#a6a6a6', fontSize: '14px', whiteSpace: 'nowrap' }}>
                  영문, 숫자만 입력 가능합니다.
                </Text>
              </div>
            </div>
          </Form.Item>

          {/* 비밀번호 */}
          <Form.Item
            label={<Text style={{ color: '#ffffff' }}>비밀번호 <span style={{ color: '#ff4d4f' }}>*</span></Text>}
            name="password"
            rules={[
              { required: true, message: '비밀번호를 입력하세요.' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();

                  // 1순위: 길이 검증 (최소/최대)
                  if (value && value.length < 6) {
                    return Promise.reject('최소 6자, 최대 20자이여야 합니다.');
                  }
                  if (value && value.length > 20) {
                    return Promise.reject('최소 6자, 최대 20자이여야 합니다.');
                  }

                  // 2순위: 패턴 검증 (영문, 숫자, 특수문자)
                  const pattern = /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!#$%&()*+,\-./:;<=>?@[\]^_{|}~₩])[A-Za-z\d!#$%&()*+,\-./:;<=>?@[\]^_{|}~₩]+$/;
                  if (value && !pattern.test(value)) {
                    return Promise.reject('영문, 숫자, 특수문자를 포함해야 합니다.');
                  }

                  return Promise.resolve();
                }
              }
            ]}
            style={{ maxWidth: '100%' }}
          >
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <Input.Password
                placeholder="비밀번호를 입력하세요."
                onChange={(e) => {
                  const newValue = e.target.value;
                  setPasswordValue(newValue);
                  form.setFieldsValue({ password: newValue });
                }}
                visibilityToggle={passwordValue.length > 0}
                iconRender={(visible) => (
                  visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
                )}
                style={{
                  height: '48px',
                  maxWidth: '600px',
                  background: '#141414',
                  border: '1px solid #303030',
                  color: '#ffffff'
                }}
              />
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                height: '48px'
              }}>
                <InfoCircleOutlined style={{ color: '#1890ff', fontSize: '16px' }} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <Text style={{ color: '#ffffff', fontSize: '12px', lineHeight: '1.2' }}>
                    길이 6~20자, 영문, 숫자, 특수문자가 각 1개 이상 포함되어야 합니다.
                  </Text>
                  <Text style={{ color: '#a6a6a6', fontSize: '11px', lineHeight: '1.2' }}>
                    {'사용 가능 특수문자: ! # $ % & ( ) * + , - . : ; < = > ? @ [ ₩ ] ^ _ { | } ~'}
                  </Text>
                </div>
              </div>
            </div>
          </Form.Item>

          {/* 이메일 */}
          <Form.Item
            label={<Text style={{ color: '#ffffff' }}>이메일 <span style={{ color: '#ff4d4f' }}>*</span></Text>}
            name="email"
            rules={[
              { required: true, message: '이메일을 입력하세요.' },
              { type: 'email', message: '이메일 형식이 올바르지 않습니다.' }
            ]}
            style={{ maxWidth: '600px' }}
          >
            <Input
              placeholder="이메일을 입력하세요."
              style={{
                height: '48px',
                background: '#141414',
                border: '1px solid #303030',
                color: '#ffffff'
              }}
            />
          </Form.Item>

          {/* 휴대전화 */}
          <Form.Item
            label={<Text style={{ color: '#ffffff' }}>휴대전화 <span style={{ color: '#ff4d4f' }}>*</span></Text>}
            name="phone"
            rules={[
              { required: true, message: '휴대전화를 입력하세요.' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();

                  // 1순위: 숫자만 입력 가능
                  if (!/^\d+$/.test(value)) {
                    return Promise.reject('숫자만 입력 가능합니다.');
                  }

                  // 2순위: 11자 형식 검증
                  if (value.length !== 11) {
                    return Promise.reject('휴대전화 형식이 올바르지 않습니다.');
                  }

                  return Promise.resolve();
                }
              }
            ]}
            style={{ maxWidth: '100%' }}
          >
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <Input
                placeholder="휴대전화를 입력하세요."
                maxLength={11}
                style={{
                  height: '48px',
                  maxWidth: '600px',
                  background: '#141414',
                  border: '1px solid #303030',
                  color: '#ffffff'
                }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <InfoCircleOutlined style={{ color: '#a6a6a6', fontSize: '16px' }} />
                <Text style={{ color: '#a6a6a6', fontSize: '14px', whiteSpace: 'nowrap' }}>
                  '-'를 제외하고 입력하세요.
                </Text>
              </div>
            </div>
          </Form.Item>

          {/* 권한 */}
          <Form.Item
            label={<Text style={{ color: '#ffffff' }}>권한 <span style={{ color: '#ff4d4f' }}>*</span></Text>}
            name="role"
            rules={[
              { required: true, message: '권한을 선택하세요.' }
            ]}
            initialValue="admin"
            style={{ maxWidth: '600px' }}
          >
            <Radio.Group>
              <Radio value="admin" style={{ color: '#ffffff' }}>Admin</Radio>
              <Radio value="super_admin" style={{ color: '#ffffff' }}>Super Admin</Radio>
            </Radio.Group>
          </Form.Item>

        </Form>
      </Card>

      {/* 하단 버튼 영역 */}
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        marginTop: '32px',
        width: '100%',
        gap: '12px'
      }}>
        <Button
          size="large"
          onClick={handleCancel}
          style={{
            minWidth: '120px',
            height: '48px',
            background: 'transparent',
            border: '1px solid #303030',
            color: '#ffffff'
          }}
        >
          취소
        </Button>
        <Button
          type="primary"
          onClick={() => form.submit()}
          loading={loading}
          size="large"
          style={{
            minWidth: '120px',
            height: '48px',
            background: '#1890ff',
            border: 'none'
          }}
        >
          추가
        </Button>
      </div>
    </Space>
  );
};

export default AddPermission;